import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert, AlertDescription } from "@/components/ui/Alert";
import { Printer, RefreshCcw, AlertCircle, Plus, CheckCircle2 } from "lucide-react";
import { printerConfigService } from "../../services/printer/printerConfigService";
import PrinterCard from "./PrinterCard";
import { useBluetoothAvailability } from "../../hooks/useBluetoothAvailability";
import { useTemporaryAlert } from "../../hooks/useTemporaryAlert";
import { getPrinterService } from '../../services/printer/MockPrinterService';

const MultiPrinterConfig = () => {
  const { showSuccess, showSuccessAlert } = useTemporaryAlert();
  const [state, setState] = useState({
    printers: [],
    isScanning: false,
    error: null,
  });

  const { isBluetoothAvailable } = useBluetoothAvailability();
  const printerService = getPrinterService(); // Usar el servicio factory

  // Función unificada para cargar impresoras usando printerConfigService
  const loadSavedPrinters = () => {
    try {
     
      const printers = printerConfigService.getAllPrinters();
     
      setState((prev) => {
       
        const newState = { ...prev, printers };
     
        return newState;
      });
    } catch (error) {
      console.error("LoadSavedPrinters - Error:", error);
      setState((prev) => ({
        ...prev,
        error: "Error al cargar las impresoras guardadas",
      }));
    }
  };

  useEffect(() => {
   
  }, [state]);

  // Cargar impresoras al montar el componente
  useEffect(() => {
    loadSavedPrinters();
  }, []);

  const handleAddPrinter = async () => {
    try {
      setState((prev) => ({ ...prev, isScanning: true, error: null }));
      console.log("1. Iniciando conexión de impresora");

      // Usar el servicio mock/real según el modo
      const printerId = await printerService.connectPrinter();
      console.log("2. PrinterId obtenido:", printerId);

      const printer = printerConfigService.addPrinter({
        id: printerId,
        name: "Nueva Impresora",
      });
      console.log("5. Impresora agregada:", printer);

      const configUpdate = printerConfigService.updatePrinterConfig(printerId, {
        printKitchenOrders: true,
        printBarOrders: true,
        autoCustomerBills: true,
        isEnabled: true,
      });
      console.log("6. Configuración actualizada:", configUpdate);

      loadSavedPrinters();
      showSuccess();
    } catch (error) {
      console.error("Error completo:", error);
      setState((prev) => ({ ...prev, error: error.message }));
    } finally {
      setState((prev) => ({ ...prev, isScanning: false }));
    }
  };

  const handleRemovePrinter = async (printerId) => {
    try {
      const success = printerConfigService.removePrinter(printerId);
      if (success) {
        loadSavedPrinters();
        showSuccess();
      }
    } catch (error) {
      console.error("Error eliminando impresora:", error);
      setState((prev) => ({
        ...prev,
        error: "Error al eliminar la impresora",
      }));
    }
  };

  const handleConfigChange = async (printerId, setting) => {
    try {
      const currentConfig = printerConfigService.printers.get(printerId)?.config || {};
      const success = printerConfigService.updatePrinterConfig(printerId, {
        ...currentConfig,
        [setting]: !currentConfig[setting],
      });

      if (success) {
        loadSavedPrinters();
        showSuccess();
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: "Error al actualizar la configuración",
      }));
    }
  };

  const handleTestPrint = async (printerId) => {
    setState((prev) => ({ ...prev, error: null }));
    try {
      // Usar el servicio mock/real según el modo
      await printerService.testPrint(printerId);
      showSuccess();
    } catch (error) {
      console.error("Error en prueba de impresión:", error);
      setState((prev) => ({
        ...prev,
        error: `Error en prueba de impresión: ${error.message}`,
      }));
    }
  };

  const isTestMode = import.meta.env.VITE_PRINTER_MODE === 'test';

  return (
    <div className="min-h-screen bg-orange-50 p-4">
      <Card className="max-w-2xl mx-auto border-orange-200">
        <CardHeader className="border-b border-orange-200 bg-orange-500">
          <CardTitle className="flex items-center justify-between text-2xl text-white">
            <div className="flex items-center">
              <Printer className="mr-2 h-6 w-6" />
              Configuración de Impresoras
              {isTestMode && <span className="ml-2 text-sm">(Modo Prueba)</span>}
            </div>
            {(isBluetoothAvailable || isTestMode) && (
              <Button
                onClick={handleAddPrinter}
                disabled={state.isScanning}
                variant="ghost"
                className="text-white hover:bg-orange-600"
              >
                {state.isScanning ? (
                  <RefreshCcw className="h-5 w-5 animate-spin" />
                ) : (
                  <Plus className="h-5 w-5" />
                )}
              </Button>
            )}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-6 pt-6">
          {!isBluetoothAvailable && !isTestMode && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Este navegador no soporta Web Bluetooth. Use Chrome en Android o
                Edge/Chrome en Windows.
              </AlertDescription>
            </Alert>
          )}

          {state.error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{state.error}</AlertDescription>
            </Alert>
          )}

          {showSuccessAlert && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <AlertDescription className="text-green-600">
                Operación realizada exitosamente
              </AlertDescription>
            </Alert>
          )}

          {state.printers.map((printer) => (
            <PrinterCard
              key={printer.id}
              printer={printer}
              onRemove={handleRemovePrinter}
              onConfigChange={handleConfigChange}
              onTestPrint={handleTestPrint}
            />
          ))}

          {state.printers.length === 0 && !state.error && (
            <div className="text-center py-8 text-gray-500">
              <Printer className="h-12 w-12 mx-auto mb-4 text-orange-300" />
              <p>No hay impresoras conectadas</p>
              <p className="text-sm mt-2">
                Presione el botón + para agregar una impresora
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MultiPrinterConfig;